/**
 * @fileoverview Supabase client initialization and small helpers for token access and cleanup.
 * @module lib/supabaseClient
 */

import { createClient, type SupabaseClient } from '@supabase/supabase-js'
import { clearOnboardingState } from '@/features/onboarding/hooks/useOnboardingState'
import { devlog } from '@/shared/utils/devlog'

/**
 * Create the Supabase client using Vite env variables.
 */
export const supabase: SupabaseClient = createClient(
  import.meta.env.VITE_SUPABASE_URL as string,
  import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
  {}
)

/**
 * Get the current access token from Supabase, if available.
 *
 * @returns Promise resolving to the JWT string or null when not authenticated
 */
export async function getAccessToken(): Promise<string | null> {
  const result = await supabase.auth.getSession()
  return result.data.session?.access_token ?? null
}

// In-memory token cache to avoid blocking interceptors.
let ACCESS_TOKEN_CACHE: string | null = null

/**
 * Get a token synchronously from memory or storage. Never blocks.
 */
export function getAccessTokenSync(): string | null {
  if (ACCESS_TOKEN_CACHE) return ACCESS_TOKEN_CACHE
  const fromStorage = getCachedAccessTokenFromStorage()
  if (fromStorage) ACCESS_TOKEN_CACHE = fromStorage
  return ACCESS_TOKEN_CACHE
}

/**
 * Get a token synchronously and report its source for debugging.
 */
export function getAccessTokenSyncWithSource(): {
  token: string | null
  source: 'memory' | 'storage' | 'none'
} {
  if (ACCESS_TOKEN_CACHE) return { token: ACCESS_TOKEN_CACHE, source: 'memory' }
  const fromStorage = getCachedAccessTokenFromStorage()
  if (fromStorage) {
    ACCESS_TOKEN_CACHE = fromStorage
    return { token: fromStorage, source: 'storage' }
  }
  return { token: null, source: 'none' }
}

/**
 * Clear user-specific cached data (org selection, onboarding state).
 * Called when detecting a user switch to prevent stale data issues.
 */
function clearUserSpecificCache(): void {
  try {
    localStorage.removeItem('activeOrgId')
    clearOnboardingState()
    devlog('auth', 'cleared user-specific cache (org, onboarding)')
  } catch {
    // Ignore storage errors
  }
}

// Keep cache in sync with Supabase auth changes (best-effort).
try {
  supabase.auth.onAuthStateChange(async (event, session) => {
    const token = session?.access_token ?? null
    ACCESS_TOKEN_CACHE = token ?? getCachedAccessTokenFromStorage()

    if (event === 'SIGNED_IN') {
      const newUserId = session?.user?.id ?? null
      const lastUserId = getLastUserId()

      // Detect user switch: different user or fresh login after another user
      if (lastUserId && newUserId && lastUserId !== newUserId) {
        devlog('auth', 'user switch detected', { from: lastUserId, to: newUserId })
        clearUserSpecificCache()
      }

      // Store the new user ID for future comparison
      if (newUserId) {
        try {
          sessionStorage.setItem('lastLoggedInUserId', newUserId)
        } catch {
          // Ignore storage errors
        }
      }
    }

    if (event === 'SIGNED_IN' || event === 'SIGNED_OUT') {
      devlog('auth', `state change ${event}`, { hasToken: Boolean(ACCESS_TOKEN_CACHE) })
    }
  })
} catch {
  // ignore in non-browser/test envs
}

/**
 * Check if a cached Supabase auth token exists in localStorage.
 * This is a fast, synchronous hint used to avoid blocking during bootstrap.
 *
 * @returns True when a cached token key is present
 */
export function hasCachedSupabaseToken(): boolean {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) return true
    }
  } catch {
    // Ignore storage access errors in non-browser environments
  }
  return false
}

/**
 * Attempt to read and parse a cached Supabase access token from localStorage.
 *
 * @returns The cached access token string or null if not present
 */
export function getCachedAccessTokenFromStorage(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const raw = localStorage.getItem(key)
        if (!raw) continue
        const parsed = JSON.parse(raw) as { access_token?: string }
        if (parsed?.access_token) return parsed.access_token
      }
    }
  } catch {
    // Ignore parse/storage errors; treat as cache miss
  }
  return null
}

/**
 * Remove Supabase auth token entries synchronously from storage.
 */
export function clearSupabaseAuthTokensSync(): void {
  try {
    const keysToRemove: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && key.startsWith('sb-') && key.endsWith('-auth-token')) {
        keysToRemove.push(key)
      }
    }
    keysToRemove.forEach(k => localStorage.removeItem(k))
  } catch {
    // Ignore storage errors
  }
}

/** Storage key for tracking the last logged-in user ID (used for cache invalidation). */
const LAST_USER_ID_KEY = 'lastLoggedInUserId'

/**
 * Get the user ID from the current session synchronously from storage.
 * Used to track user identity for cache invalidation on user switch.
 */
export function getUserIdFromStoredSession(): string | null {
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (!key) continue
      if (key.startsWith('sb-') && key.endsWith('-auth-token')) {
        const raw = localStorage.getItem(key)
        if (!raw) continue
        const parsed = JSON.parse(raw) as { user?: { id?: string } }
        if (parsed?.user?.id) return parsed.user.id
      }
    }
  } catch {
    // Ignore parse/storage errors
  }
  return null
}

/**
 * Store the current user ID before logout for comparison on next login.
 * This enables keeping cache for same-user re-login while clearing for different users.
 */
export function storeLastUserId(): void {
  try {
    const userId = getUserIdFromStoredSession()
    if (userId) {
      sessionStorage.setItem(LAST_USER_ID_KEY, userId)
    }
  } catch {
    // Ignore storage errors
  }
}

/**
 * Get the last logged-in user ID from sessionStorage.
 */
export function getLastUserId(): string | null {
  try {
    return sessionStorage.getItem(LAST_USER_ID_KEY)
  } catch {
    return null
  }
}

/**
 * Clear the stored last user ID.
 */
export function clearLastUserId(): void {
  try {
    sessionStorage.removeItem(LAST_USER_ID_KEY)
  } catch {
    // Ignore storage errors
  }
}
